import React, { useEffect, useRef } from 'react';

/**
 * This hook provides a way to retrieve only the last value (after the delay has been passed without any change)
 * To put it in context, if the user is typing within a search box, the debounced value will be returned when
 * they've stopped typing for more than 200ms (delay).
 * @param value value to debounce
 * @param delay delay
 */
export function useDebounce<T = string>(value: T, delay = 200) {
    // State and setters for debounced value
    const [debouncedValue, setDebouncedValue] = React.useState(value);

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export function useDebouncedCallback<T = string>(fn?: (value: T) => void) {
    const [value, setValue] = React.useState<T>();
    const debouncedValue = useDebounce(value);
    const fnRef = useRef(fn);

    useEffect(() => {
        if (debouncedValue !== undefined) {
            if (fnRef.current) fnRef.current(debouncedValue);
        }
    }, [debouncedValue]);

    useEffect(() => {
        fnRef.current = fn;
    }, [fn]);

    return { debouncedFunction: (v: T) => setValue(v), value };
}
